/**
 * Component for uploading documents to a case
 */
import React, { useState, useRef } from 'react';
import { Button, Modal } from '../../../shared/components';
import { uploadDocumentToCase } from '../api/caseApi';
import { formatFileSize } from '../../../shared/utils/helpers';

/**
 * Props for the UploadDocument component
 */
interface UploadDocumentProps {
  /**
   * ID of the case to upload documents to
   */
  caseId: string;
  
  /**
   * Callback function called when a document is successfully uploaded
   */
  onSuccess: () => void;
}

/**
 * Component that allows users to upload documents to a case
 */
export function UploadDocument({ caseId, onSuccess }: UploadDocumentProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Open the modal
   */
  const handleOpen = () => {
    setIsOpen(true);
    setSelectedFile(null);
    setDocumentTitle('');
    setError(null);
  };

  /**
   * Close the modal and reset state
   */
  const handleClose = () => {
    setIsOpen(false);
  };

  /**
   * Handle file selection
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      
      // Auto-populate title from filename (without extension)
      const fileName = file.name.split('.').slice(0, -1).join('.');
      setDocumentTitle(fileName);
    }
  };

  /**
   * Handle document title change
   */
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentTitle(e.target.value);
  };

  /**
   * Trigger file input click
   */
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    
    if (!documentTitle.trim()) {
      setError('Please enter a document title');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await uploadDocumentToCase(caseId, selectedFile, documentTitle);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        onClick={handleOpen}
        startIcon={
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        }
      >
        Upload
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Upload Document"
        size="md"
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Document File
              </label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              
              {selectedFile ? (
                <div className="mt-1 flex items-center p-3 border border-gray-300 rounded-md bg-gray-50">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="light"
                    size="sm"
                    onClick={triggerFileInput}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <div
                  className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:bg-gray-50"
                  onClick={triggerFileInput}
                >
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Upload a file</span>
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, Word, or Image up to 10MB
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <label htmlFor="document-title" className="block text-sm font-medium text-gray-700">
                Document Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="document-title"
                value={documentTitle}
                onChange={handleTitleChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter document title"
              />
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <Button
              type="button"
              variant="light"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting || !selectedFile || !documentTitle.trim()}
            >
              Upload
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
